import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DollarSign, FileText, RefreshCw, Mail, MessageSquare, CreditCard, ExternalLink } from "lucide-react";

type Invoice = {
  id: string;
  customer_id: string | null;
  job_id: string | null;
  customer_name: string;
  amount: number;
  status: string;
  pay_method: string | null;
  issued_on: string;
  due_on: string | null;
  paid_on: string | null;
  payment_url: string | null;
  last_sent_at: string | null;
  customers: { email: string | null; phone: string | null } | null;
};

type Props = { companyId: string; isOwner: boolean };

const todayISO = () => new Date().toISOString().slice(0, 10);

const daysBetween = (from: string, to: string) =>
  Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);

/** Whole days past the due date (falls back to issued date). */
const daysLate = (inv: { due_on: string | null; issued_on: string }) => {
  const due = new Date(inv.due_on ?? inv.issued_on).getTime();
  return Math.floor((Date.now() - due) / 864e5);
};

const agingBucket = (inv: { due_on: string | null; issued_on: string }) => {
  const ref = inv.due_on ?? inv.issued_on;
  const late = daysBetween(ref, todayISO());
  if (late <= 0) return "current";
  if (late <= 30) return "1–30";
  if (late <= 60) return "31–60";
  return "60+";
};

const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  paid: "secondary",
  unpaid: "destructive",
  void: "outline",
};

export default function Billing({ companyId, isOwner }: Props) {
  const [rows, setRows] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "unpaid" | "overdue" | "paid" | "chase">("unpaid");
  const [sending, setSending] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("invoices")
      .select(
        "id, customer_id, job_id, customer_name, amount, status, pay_method, issued_on, due_on, paid_on, payment_url, last_sent_at, customers!left(email, phone)"
      )
      .eq("company_id", companyId)
      .order("issued_on", { ascending: false })
      .limit(500);
    if (error) toast.error("Could not load invoices");
    setRows(((data as unknown as Invoice[]) ?? []).map((r) => ({ ...r, amount: Number(r.amount) })));
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /** Create an invoice for every completed job that doesn't have one yet. */
  const generate = async () => {
    setGenerating(true);
    const [{ data: jobs, error: jobsError }, { data: existing }] = await Promise.all([
      supabase
        .from("jobs")
        .select("id, customer_id, customer_name, scheduled_date, status, customers!left(price, pay_method)")
        .eq("company_id", companyId)
        .like("status", "complete%")
        .limit(1000),
      supabase.from("invoices").select("job_id").eq("company_id", companyId).limit(2000),
    ]);
    if (jobsError) {
      setGenerating(false);
      toast.error("Could not read completed jobs");
      return;
    }
    const invoiced = new Set((existing ?? []).map((r) => r.job_id).filter(Boolean));
    const pending = (jobs ?? []).filter((j) => !invoiced.has(j.id));
    if (pending.length === 0) {
      setGenerating(false);
      toast.info("Every completed job is already invoiced");
      return;
    }
    const payload = pending.map((j) => {
      const cust = (j as unknown as { customers: { price: number | null; pay_method: string | null } | null })
        .customers;
      const paid = j.status === "completed_paid";
      return {
        company_id: companyId,
        customer_id: j.customer_id,
        job_id: j.id,
        customer_name: j.customer_name,
        amount: Number(cust?.price ?? 0),
        status: paid ? "paid" : "unpaid",
        pay_method: cust?.pay_method ?? null,
        issued_on: j.scheduled_date,
        due_on: new Date(new Date(`${j.scheduled_date}T00:00:00`).getTime() + 14 * 86400000)
          .toISOString()
          .slice(0, 10),
        paid_on: paid ? j.scheduled_date : null,
      };
    });
    const { error } = await supabase.from("invoices").insert(payload);
    setGenerating(false);
    if (error) {
      toast.error("Could not create invoices");
      return;
    }
    toast.success(`${payload.length} invoice${payload.length === 1 ? "" : "s"} created`);
    load();
  };

  const setStatus = async (inv: Invoice, status: string) => {
    const patch = {
      status,
      paid_on: status === "paid" ? new Date().toISOString().slice(0, 10) : null,
    };
    setRows((prev) => prev.map((r) => (r.id === inv.id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("invoices").update(patch).eq("id", inv.id);
    if (error) {
      toast.error("Could not update invoice");
      load();
      return;
    }
    // Keep the job's status in step so the schedule shows the same truth.
    if (inv.job_id && status !== "void") {
      await supabase
        .from("jobs")
        .update({ status: status === "paid" ? "completed_paid" : "completed_unpaid" })
        .eq("id", inv.job_id);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "overdue") {
        if (r.status !== "unpaid" || agingBucket(r) === "current") return false;
      } else if (filter === "chase") {
        // Unpaid, past due, and not already nudged in the last 3 days.
        if (r.status !== "unpaid" || daysLate(r) < 1) return false;
        if (r.last_sent_at && Date.now() - new Date(r.last_sent_at).getTime() < 3 * 864e5) {
          return false;
        }
      } else if (filter !== "all" && r.status !== filter) {
        return false;
      }
      if (!q) return true;
      return r.customer_name.toLowerCase().includes(q);
    });
  }, [rows, query, filter]);

  const unpaid = rows.filter((r) => r.status === "unpaid");

  const aging = useMemo(() => {
    const buckets: Record<string, number> = { current: 0, "1–30": 0, "31–60": 0, "60+": 0 };
    unpaid.forEach((r) => {
      buckets[agingBucket(r)] += r.amount;
    });
    return buckets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const balances = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number; oldest: string }>();
    unpaid.forEach((r) => {
      const key = r.customer_id ?? r.customer_name;
      const prev = map.get(key);
      map.set(key, {
        name: r.customer_name,
        total: (prev?.total ?? 0) + r.amount,
        count: (prev?.count ?? 0) + 1,
        oldest: prev && prev.oldest < r.issued_on ? prev.oldest : r.issued_on,
      });
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total).slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const reminderText = (r: Invoice) =>
    `Hi ${r.customer_name.split(" ")[0]} — a quick reminder that your lawn service invoice for ${money(
      r.amount
    )} (issued ${r.issued_on}${r.due_on ? `, due ${r.due_on}` : ""}) is still open. Thank you!`;

  const emailInvoice = (r: Invoice) => {
    if (!r.customers?.email) return toast.error("No email on file for this customer");
    window.location.href = `mailto:${r.customers.email}?subject=${encodeURIComponent(
      `Invoice ${money(r.amount)} — lawn service ${r.issued_on}`
    )}&body=${encodeURIComponent(reminderText(r))}`;
  };

  /** Create a Stripe checkout link for the invoice and text it to the customer. */
  const sendPaymentLink = async (r: Invoice) => {
    setSending(r.id);
    const { data, error } = await supabase.functions.invoke("create-invoice-payment-link", {
      body: { invoice_id: r.id, send_sms: true },
    });
    setSending(null);
    const payload = data as { payment_url?: string; error?: string; sms?: { error?: string; skipped?: string } } | null;
    if (error || payload?.error) {
      toast.error(payload?.error ?? "Could not create the payment link");
      return;
    }
    setRows((prev) =>
      prev.map((row) =>
        row.id === r.id
          ? { ...row, payment_url: payload?.payment_url ?? row.payment_url, last_sent_at: new Date().toISOString() }
          : row
      )
    );
    if (payload?.sms?.error) toast.warning("Payment link ready, but the text failed to send");
    else if (payload?.sms?.skipped) toast.success("Payment link ready (customer texts are off)");
    else toast.success(`Payment link texted to ${r.customer_name.split(" ")[0]}`);
  };

  const textInvoice = (r: Invoice) => {
    const phone = r.customers?.phone?.replace(/[^\d+]/g, "");
    if (!phone) return toast.error("No phone on file for this customer");
    window.location.href = `sms:${phone}?&body=${encodeURIComponent(reminderText(r))}`;
  };

  const outstanding = rows.filter((r) => r.status === "unpaid").reduce((s, r) => s + r.amount, 0);
  const month = new Date().toISOString().slice(0, 7);
  const collected = rows
    .filter((r) => r.status === "paid" && (r.paid_on ?? "").startsWith(month))
    .reduce((s, r) => s + r.amount, 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Outstanding</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-bold text-destructive">{money(outstanding)}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Collected this month</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-bold text-primary">{money(collected)}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Unpaid invoices</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-bold">
            {rows.filter((r) => r.status === "unpaid").length}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3">
        <Card>

          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Aging (unpaid)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-4 gap-2 text-center text-xs">
            {(["current", "1–30", "31–60", "60+"] as const).map((b) => (
              <div key={b}>
                <div className="text-muted-foreground">{b === "current" ? "Not due" : `${b} days`}</div>
                <div className={b === "current" ? "font-semibold" : "font-semibold text-destructive"}>
                  {money(aging[b])}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Customer balances</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-xs">
            {balances.length === 0 ? (
              <p className="text-muted-foreground">Nothing outstanding.</p>
            ) : (
              balances.map((b) => (
                <div key={b.name} className="flex items-center justify-between gap-2">
                  <span className="truncate">{b.name}</span>
                  <span className="whitespace-nowrap text-muted-foreground">
                    {b.count} open · oldest {b.oldest}
                  </span>
                  <span className="font-semibold">{money(b.total)}</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" /> Invoices
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 w-44"
              placeholder="Search customer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {(["unpaid", "overdue", "chase", "paid", "all"] as const).map((f) => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? "default" : "outline"}
                className="h-8 capitalize"
                onClick={() => setFilter(f)}
              >
                {f === "chase" ? "Chase this week" : f}
              </Button>
            ))}
            <Button size="sm" variant="outline" className="h-8" onClick={load}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
            </Button>
            {isOwner && (
              <Button size="sm" className="h-8" disabled={generating} onClick={generate}>
                <DollarSign className="mr-1 h-3.5 w-3.5" />
                {generating ? "Working…" : "Bill completed jobs"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="py-6 text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              No invoices here yet. Complete jobs on the schedule, then click “Bill completed jobs”.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr className="[&>th]:px-2 [&>th]:py-1">
                  <th>Customer</th>
                  <th>Issued</th>
                  <th>Due</th>
                  <th>Days late</th>
                  <th>Reminded</th>
                  <th className="text-right">Amount</th>
                  <th>Pay method</th>
                  <th>Status</th>
                  <th>Paid on</th>
                  {isOwner && <th className="text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-border even:bg-muted/40 [&>td]:px-2 [&>td]:py-1">
                    <td className="font-medium">{r.customer_name}</td>
                    <td className="text-muted-foreground">{r.issued_on}</td>
                    <td
                      className={
                        r.status === "unpaid" && agingBucket(r) !== "current"
                          ? "font-medium text-destructive"
                          : "text-muted-foreground"
                      }
                    >
                      {r.due_on ?? "—"}
                    </td>
                    <td className={r.status === "unpaid" && daysLate(r) > 0 ? "font-medium text-destructive" : "text-muted-foreground"}>
                      {r.status === "unpaid" && daysLate(r) > 0 ? `${daysLate(r)}d` : "—"}
                    </td>
                    <td className="text-muted-foreground">
                      {r.last_sent_at ? new Date(r.last_sent_at).toLocaleDateString() : "Never"}
                    </td>
                    <td className="text-right">{money(r.amount)}</td>
                    <td className="text-muted-foreground">
                      {r.pay_method === "Mowzi" ? "Mowzi" : r.pay_method ?? "—"}
                    </td>
                    <td>
                      <Badge variant={STATUS_VARIANT[r.status] ?? "outline"} className="text-[10px]">
                        {r.status}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground">{r.paid_on ?? "—"}</td>
                    {isOwner && (
                      <td className="whitespace-nowrap text-center">
                        {r.status !== "paid" && (
                          <Button
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            onClick={() => setStatus(r, "paid")}
                          >
                            Mark paid
                          </Button>
                        )}
                        {r.status === "paid" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[11px]"
                            onClick={() => setStatus(r, "unpaid")}
                          >
                            Mark unpaid
                          </Button>
                        )}
                        {r.status === "unpaid" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="ml-1 h-6 px-2 text-[11px]"
                              disabled={sending === r.id}
                              onClick={() => sendPaymentLink(r)}
                            >
                              <CreditCard className="mr-1 h-3.5 w-3.5" />
                              {sending === r.id ? "Sending…" : r.payment_url ? "Resend link" : "Payment link"}
                            </Button>
                            {r.payment_url && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                aria-label={`Open payment link for ${r.customer_name}`}
                                asChild
                              >
                                <a href={r.payment_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="ml-1 h-6 w-6"
                              aria-label={`Email invoice to ${r.customer_name}`}
                              onClick={() => emailInvoice(r)}
                            >
                              <Mail className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              aria-label={`Text invoice to ${r.customer_name}`}
                              onClick={() => textInvoice(r)}
                            >
                              <MessageSquare className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {r.status !== "void" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-1 h-6 px-2 text-[11px]"
                            onClick={() => setStatus(r, "void")}
                          >
                            Void
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
